// import { useGroups } from '@/libs/layers/communities/groups-layer/composables/useGroups'
import { computed, toValue } from 'vue'

import { useAuth } from '../../../common/auth-layer/composables/useAuth'
import { useGlobal } from '../../core-layer/composables/useGlobal'
import { CurrentUserService } from '../services/currentUser.service'
// current-user-layer/composables/useCurrentUser.ts
import { useCurrentUserStore } from '../stores/currentUser.store'

// import { useGroups } from '@/libs/layers/communities/groups-layer/composables/useGroups'
import { useLocationStore } from '../../location-layer/store/location.store'
import type { CrmContactAddressUpdate, CurrentUserProfile, CurrentUserProfileV2 } from '../types/currentUser'

function errorMessageFromUnknown(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (typeof error === 'string' && error.length > 0) {
    return error
  }
  return fallback
}

export const useCurrentUser = () => {
  const store = useCurrentUserStore()
  const auth = useAuth()
  const { isGokollab } = useGlobal()
  const service = new CurrentUserService()

  /**
   * Platform chat id for the logged-in user.
   * GoKollab → contactId. UserV2 (clientportal) → userId.
   */
  const currentUserId = computed(() => {
    if (toValue(isGokollab)) {
      return auth?.store?.contactId?.trim() || ''
    }

    return auth?.store?.userId?.trim() || ''
  })

  const getCurrentUserId = (): string => currentUserId.value

  /**
   * Trusted analytics session identity. Auth state is available before the profile request settles,
   * so callers that only need an authentication gate do not need to infer it from profile hydration.
   */
  const getAuthenticatedContactId = (): string | null => {
    const contactId = auth?.store?.contactId?.trim()
    return auth?.store?.isLoggedIn && contactId ? contactId : null
  }

  // Watch auth store changes and automatically load user data when authenticated

  const ensureLoaded = async () => {
    // Check if user is authenticated before proceeding
    if (!auth?.store?.isLoggedIn) {
      return null
    }

    const contactId = auth?.store?.contactId

    if (!contactId) return null

    // Prepare parallel fetch operations using existing helper functions
    const fetchOperations: Promise<unknown>[] = []

    // Fetch profile if not already loaded
    if (!store.profile) {
      fetchOperations.push(loadProfile())
    }

    // Execute all fetch operations in parallel
    if (fetchOperations.length > 0) {
      await Promise.all(fetchOperations)
    }

    return store.profile
  }

  const loadProfile = async (force = false) => {
    // Check if user is authenticated before proceeding
    if (!auth?.store?.isLoggedIn) {
      return null
    }

    const { isGokollab } = useGlobal()
    const locationStore = useLocationStore()
    // Skip if profile is already loaded and not forcing refresh
    if (!force && store.hasProfile) {
      return store.profile
    }

    if (!force && store.isLoadingProfile) {
      return store.profile
    }

    let userIdentifier
    if (isGokollab) {
      userIdentifier = auth?.store?.userId
    } else {
      userIdentifier = auth?.store?.contactId
    }

    if (!userIdentifier) return null
    store.setLoadingProfile(true)
    store.setError(null)
    try {
      // if gokollab, use the new endpoint
      if (isGokollab || locationStore.isAuthV2Enabled) {
        const p = await service.getUserV2Details(userIdentifier)
        store.setProfile(p)
        return p
      } else {
        const p = await service.getContactDetails(userIdentifier)
        store.setProfile(p)
        return p
      }
    } catch (e: unknown) {
      store.setError(errorMessageFromUnknown(e, 'Failed to load profile'))
      throw e
    } finally {
      store.setLoadingProfile(false)
    }
  }

  const loadGokollabUser = async (offerLocationId: string) => {
    try {
      const gokollabUser = await service.getGokollabUser(offerLocationId)
      store.setGokollabUser(gokollabUser)
      return gokollabUser
    } catch (e: unknown) {
      store.setError(errorMessageFromUnknown(e, 'Failed to load gokollab user'))
      throw e
    }
  }

  const getWhiteLabelUserContact = async (entityLocationId: string, signal?: AbortSignal, groupId?: string) => {
    return await service.getWhiteLabelUserContact(entityLocationId, signal, groupId)
  }

  const updateCrmContactAddress = async (locationId: string, contactId: string, address: CrmContactAddressUpdate) => {
    return await service.updateCrmContactAddress(locationId, contactId, address)
  }

  // Helper methods for profile state
  const hasProfile = () => store.hasProfile
  const updateProfile = async (profileData: Partial<CurrentUserProfile | CurrentUserProfileV2>) => {
    const { isGokollab } = useGlobal()
    const locationStore = useLocationStore()

    try {
      store.setLoadingProfile(true)

      let updatedProfile: CurrentUserProfile | CurrentUserProfileV2
      if (isGokollab || locationStore.isAuthV2Enabled) {
        const userId = auth?.store?.userId
        if (!userId) throw new Error('User ID not found')
        updatedProfile = await service.updateUserV2Details(userId, profileData)
      } else {
        const contactId = auth?.store?.contactId
        if (!contactId) throw new Error('Contact ID not found')
        updatedProfile = await service.updateContactDetails(contactId, profileData)
      }

      // Merge so a partial API response cannot replace the full cached profile (PUT may echo only changed fields).
      store.setProfile({
        ...(store.profile ?? {}),
        ...updatedProfile,
      } as CurrentUserProfile | CurrentUserProfileV2)

      return updatedProfile
    } catch (error) {
      console.error('Failed to update profile:', error)
      throw error
    } finally {
      store.setLoadingProfile(false)
    }
  }

  const changePassword = async (passwordData: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
    contactId: string
    revokeAllSessions?: boolean
  }) => {
    const contactId = passwordData.contactId || auth?.store?.contactId
    if (!contactId) return null
    return await service.changePassword(passwordData)
  }
  return {
    // Expose store directly for consumers
    store,
    currentUserId,
    getCurrentUserId,
    getAuthenticatedContactId,
    ensureLoaded,
    loadProfile,
    loadGokollabUser,
    getWhiteLabelUserContact,
    updateCrmContactAddress,
    // Profile state helpers
    hasProfile,
    updateProfile,
    changePassword,
  }
}
